package com.trevorwiebe.timesheet.mileage.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import kotlinx.browser.document
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.Image
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.FileReader

private const val MAX_IMAGE_WIDTH = 1600

@Composable
actual fun rememberImagePickerLauncher(onImagesSelected: (List<ByteArray>) -> Unit): () -> Unit {
    // The change listener is installed once, so read the latest callback through this.
    val onSelected by rememberUpdatedState(onImagesSelected)
    val fileInput = remember {
        (document.createElement("input") as HTMLInputElement).apply {
            type = "file"
            accept = "image/*"
            multiple = true
            asDynamic().style.display = "none"
        }
    }

    DisposableEffect(Unit) {
        document.body?.appendChild(fileInput)
        fileInput.onchange = {
            val files = fileInput.files
            val count = files?.length ?: 0
            if (files != null && count > 0) {
                val pages = arrayOfNulls<ByteArray>(count)
                var remaining = count
                for (i in 0 until count) {
                    toJpeg(files.item(i)!!) { bytes ->
                        pages[i] = bytes
                        remaining--
                        if (remaining == 0) {
                            val converted = pages.filterNotNull()
                            if (converted.isNotEmpty()) onSelected(converted)
                        }
                    }
                }
            }
            fileInput.value = ""
            Unit
        }
        onDispose {
            document.body?.removeChild(fileInput)
        }
    }

    return { fileInput.click() }
}

/** Re-encodes any browser-readable image as a JPEG at most [MAX_IMAGE_WIDTH] wide; null on failure. */
private fun toJpeg(file: Blob, onDone: (ByteArray?) -> Unit) {
    val url = URL.createObjectURL(file)
    val image = Image()
    image.onload = {
        URL.revokeObjectURL(url)
        val scale = minOf(1.0, MAX_IMAGE_WIDTH.toDouble() / image.naturalWidth)
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = (image.naturalWidth * scale).toInt()
        canvas.height = (image.naturalHeight * scale).toInt()
        (canvas.getContext("2d") as CanvasRenderingContext2D)
            .drawImage(image, 0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
        canvas.toBlob({ blob ->
            if (blob == null) {
                onDone(null)
            } else {
                val reader = FileReader()
                reader.onload = { _ ->
                    onDone(Int8Array(reader.result as ArrayBuffer).unsafeCast<ByteArray>())
                }
                reader.readAsArrayBuffer(blob)
            }
        }, "image/jpeg", 0.85)
    }
    image.asDynamic().onerror = {
        URL.revokeObjectURL(url)
        onDone(null)
    }
    image.src = url
}
